// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Block from "@store/Library/Components/Toolkit/Block";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

const contentZoneName = "Content";

const enum fields {
    title = "title",
    subtitle = "subtitle",
}

export interface AccountWelcomeProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subtitle]: string;
    };
}

export interface AccountWelcomeStyles {
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
}

const accountWelcomeStyles: AccountWelcomeStyles = {
    title: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: ${pxToRem(3)};
        `,
    },
    subtitle: {
        variant: "p",
        css: css`
            font-size: ${x => x.theme.design.typography.size.base};
            text-align: center;

            ${breakpoint("md")} {
                font-size: ${x => x.theme.design.typography.size.large};
            }

            ${breakpoint("xl")} {
                font-size: ${x => x.theme.design.typography.size.larger};
            }
        `,
    },
};

const styles = accountWelcomeStyles;

const AccountWelcome: FC<AccountWelcomeProps> = ({ id, fields: { title: baseTitle, subtitle } }) => {
    const { isInShell } = useShellContext();
    const hasContent = useZoneIsActive(id || "", contentZoneName);
    const displayName = useAppSelector(state => state.context.session.firstName || state.context.session.userName);
    const isSignedIn =
        useAppSelector(state => state.context.session.userName && !getCurrentUserIsGuest(state)) || isInShell;

    const title = useMemo(() => {
        if (baseTitle.includes("{0}")) {
            if (!displayName) {
                return baseTitle.replace(/\s*\{0\}\s*/, "");
            }

            return baseTitle.replace("{0}", displayName);
        }

        return baseTitle;
    }, [displayName, baseTitle]);

    if (!isSignedIn) {
        return null;
    }

    return (
        <Block>
            {title && <Typography {...styles.title}>{title}</Typography>}
            {subtitle && <Typography {...styles.subtitle}>{subtitle}</Typography>}
            {hasContent && <Zone contentId={id} zoneName={contentZoneName} />}
        </Block>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: AccountWelcome,
    definition: {
        group: "My Account",
        displayName: "Account Welcome",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Hi {0},",
            },
            {
                name: fields.subtitle,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
